import {
  addDays, addMonths, diffDays, ProjectionAssumptions, ProjectionInput, resolvedProjectionAssumptionsSchema,
  type ProjectionAssumptionsData, type ProjectionInputData, type ResolvedProjectionAssumptions, type TenantSettings, type ProjectionAssumptionKey,
} from '@dairy/shared';
import { woodA, woodAForPeak, woodY } from './forecast';
import { projectionInputErrors } from './projection-validation';

export type { ProjectionAssumptionKey } from '@dairy/shared';
type ValueMetadata = { source: 'OWNER' | 'FARM' | 'BUYER_PRICE' | 'SETTING' | 'NONE'; from?: string; to?: string; sample?: number; sampleUnit?: string };
export type ProjectionFarmDefaults = Partial<Record<ProjectionAssumptionKey, ValueMetadata & { value: number | null }>>;
export interface AssumptionResolution {
  values: Record<ProjectionAssumptionKey, ValueMetadata & { value: number | null }>;
  missing: ProjectionAssumptionKey[];
  assumptions: ResolvedProjectionAssumptions | null;
}

/** IP-1: explicit owner values (including zero) win; missing inputs never become guesses. */
export function resolveAssumptions(entered: ProjectionAssumptionsData, farmDefaults: ProjectionFarmDefaults, settings: TenantSettings): AssumptionResolution {
  const parsed = ProjectionAssumptions.parse(entered);
  const fallback: Partial<Record<ProjectionAssumptionKey, number>> = {
    calfMilkMlPerDay: 4000, calfMilkDays: 90, servicesPerConceptionX10: 20,
    laborPerMonthSantim: 0, maturePeakMl: Math.round(settings.defaultPeakLitersPerDay * 1000),
  };
  const keys = Object.keys(ProjectionAssumptions.omit({ scenarios: true }).shape) as ProjectionAssumptionKey[];
  const values = Object.fromEntries(keys.map((key) => {
    if (parsed[key] !== null) return [key, { value: parsed[key], source: 'OWNER' }];
    const farm = farmDefaults[key];
    if (farm?.value != null && farm.source !== 'NONE') return [key, { ...farm }];
    if (fallback[key] !== undefined) return [key, { value: fallback[key], source: 'SETTING' }];
    return [key, { value: null, source: 'NONE' }];
  })) as AssumptionResolution['values'];
  // Farm measurements can fall outside the supported input ranges (for example a zero peak).
  // Keep those values visible in the farm column, but require a usable override for calculation.
  const shapes = ProjectionAssumptions.omit({ scenarios: true }).shape;
  for (const key of keys) if (values[key].value !== null && !shapes[key].unwrap().safeParse(values[key].value).success) values[key] = { ...values[key], value: null, source: 'NONE' };
  const missing = keys.filter((key) => values[key].value === null);
  return { values, missing, assumptions: missing.length ? null : resolvedProjectionAssumptionsSchema.parse({ ...values, scenarios: parsed.scenarios }) };
}

export type ProjectionScenarioKind = 'LOW' | 'EXPECTED' | 'HIGH';
const moneyLines = ['milkIncomeSantim', 'calfSalesSantim', 'feedSantim', 'calfMilkSantim', 'youngStockFeedSantim', 'vetSantim', 'laborSantim', 'breedingSantim', 'netSantim'] as const;
type MoneyLine = typeof moneyLines[number];
export type ProjectionTotals = Record<MoneyLine, number> & { producedMl: number; soldMl: number; calfMilkMl: number };
export type ProjectionMonth = ProjectionTotals & { month: number; from: string; to: string; cumulativeSantim: number };
export interface ProjectionResult {
  scenario: ProjectionScenarioKind;
  months: ProjectionMonth[];
  totals: ProjectionTotals;
  operatingProfitSantim: number;
  netAfterPurchaseSantim: number;
  paybackMonth: number | null;
  breakEvenMilkPriceSantim: number | null;
  impliedPeakMl: number;
  conceptions: string[];
  calvings: string[];
  dryOffs: string[];
  rearing: { firstCalving: string; ageAtFirstCalvingMonths: number; costToFirstCalvingSantim: number; costIncludingPurchaseSantim: number } | null;
}

/** All money multiplication/division stays integral, including half-up ties. */
function rounded(numerator: bigint, denominator: bigint): number {
  const value = Number((2n * numerator + denominator) / (2n * denominator));
  if (!Number.isSafeInteger(value)) throw new Error('Projection exceeds safe integer range');
  return value;
}
const scale = (amount: number, bp: number) => rounded(BigInt(amount) * BigInt(bp), 10000n);
const milkMoney = (ml: number, price: number) => rounded(BigInt(ml) * BigInt(price), 1000n);
const annualCost = (amount: number, days: number, multiplier = 1) => rounded(BigInt(amount) * BigInt(days) * BigInt(multiplier), 365n);
const later = (a: string, b: string) => a > b ? a : b;
type Bucket = { days: number; cowDays: number; calfDays: number; youngDays: number; heiferDays: number; producedMl: number; soldMl: number; calfMilkMl: number; calves: number; aiSantim: number };
const bucket = (): Bucket => ({ days: 0, cowDays: 0, calfDays: 0, youngDays: 0, heiferDays: 0, producedMl: 0, soldMl: 0, calfMilkMl: 0, calves: 0, aiSantim: 0 });

/** D-15. Month intervals are purchase-date anchored, with exclusive end dates. */
export function projectAnimal(rawInput: ProjectionInputData, rawAssumptions: ResolvedProjectionAssumptions, settings: TenantSettings, scenario: ProjectionScenarioKind = 'EXPECTED'): ProjectionResult {
  const input = ProjectionInput.parse(rawInput);
  const issues = projectionInputErrors(input, settings);
  if (issues.length) throw new Error(`Invalid projection input: ${issues.join(', ')}`);
  const assumptions = resolvedProjectionAssumptionsSchema.parse(rawAssumptions);
  const a = Object.fromEntries(Object.entries(assumptions).filter(([key]) => key !== 'scenarios').map(([key, value]) => [key, (value as { value: number }).value])) as Record<ProjectionAssumptionKey, number>;
  const sc = scenario === 'LOW' ? assumptions.scenarios.low : scenario === 'HIGH' ? assumptions.scenarios.high : { yieldBp: 10000, priceBp: 10000, feedBp: 10000, spcAddX10: 0 };
  const price = scale(a.milkPriceSantim, sc.priceBp);
  const feedCow = scale(a.feedPerCowDaySantim, sc.feedBp), feedLiter = scale(a.feedPerLiterSantim, sc.feedBp);
  const calfFeed = scale(a.calfFeedPerDaySantim, sc.feedBp), youngFeed = scale(a.youngFeedPerDaySantim, sc.feedBp), heiferFeed = scale(a.heiferFeedPerDaySantim, sc.feedBp);
  const spc = Math.max(10, a.servicesPerConceptionX10 + sc.spcAddX10);
  const delay = rounded(BigInt(spc - 10) * 21n, 10n);
  const aiCost = rounded(BigInt(a.aiCostPerServiceSantim) * BigInt(spc), 10n);
  const calfValue = rounded(BigInt(a.calfValueFemaleSantim) + BigInt(a.calfValueMaleSantim), 2n);
  const start = input.startDate, end = addMonths(start, input.horizonMonths);
  const isCow = input.kind === 'COW';
  const currentCalving = isCow && input.milking ? addDays(start, -input.dimNow!) : null;
  const firstA = isCow
    ? input.milking ? woodA(input.litersNowMl! / 1000, input.dimNow!, settings.woodB, settings.woodC) : woodAForPeak((input.lastPeakMl ?? a.maturePeakMl) / 1000, settings.woodB, settings.woodC)
    : woodAForPeak((input.expectedPeakMl ?? Math.round(a.maturePeakMl * settings.firstLactationFactor)) / 1000, settings.woodB, settings.woodC);
  const matureA = isCow ? firstA : firstA / settings.firstLactationFactor;
  const suppliedService = input.serviceDate ?? (input.monthsPregnant !== undefined ? addDays(start, -input.monthsPregnant * 30) : null);
  const breedingStart = isCow ? currentCalving ? later(start, addDays(currentCalving, settings.breedingStartDays)) : start : later(start, addMonths(input.birthDate, settings.heiferBreedingAgeMonths));
  const firstConception = suppliedService ?? addDays(breedingStart, delay);
  const timeline = [{ conception: firstConception, calving: addDays(firstConception, settings.gestationDays), charged: !suppliedService }];
  const firstCalving = isCow ? null : timeline[0]!.calving;
  const simulationEnd = firstCalving ? later(end, addDays(firstCalving, 1)) : end;
  for (;;) {
    const conception = addDays(timeline[timeline.length - 1]!.calving, settings.breedingStartDays + delay);
    if (conception >= simulationEnd) break;
    timeline.push({ conception, calving: addDays(conception, settings.gestationDays), charged: true });
  }
  const calvings = timeline.map((event) => event.calving);
  const lactations = [...(currentCalving ? [currentCalving] : []), ...calvings];
  const aiDates = new Set(timeline.filter((event) => event.charged).map((event) => event.conception));
  const monthStarts = Array.from({ length: input.horizonMonths + 1 }, (_, m) => addMonths(start, m));
  const buckets = Array.from({ length: input.horizonMonths }, bucket);
  const rear = bucket();
  let monthIndex = 0;
  for (let day = start; day < simulationEnd; day = addDays(day, 1)) {
    while (monthIndex < input.horizonMonths && day >= monthStarts[monthIndex + 1]!) monthIndex++;
    const lactation = lactations.filter((date) => date <= day).at(-1);
    const calved = isCow || lactation !== undefined;
    let ml = 0, colostrum = false;
    if (lactation) {
      const nextCalving = calvings.find((date) => date > lactation);
      if (!nextCalving || day <= addDays(nextCalving, -settings.dryPeriodDays)) {
        const curveA = !isCow && lactation === firstCalving ? firstA : matureA;
        const dim = diffDays(day, lactation);
        const liters = woodY(curveA * sc.yieldBp / 10000, dim, settings.woodB, settings.woodC);
        if (liters >= 1) { ml = Math.round(liters * 1000); colostrum = dim < settings.colostrumDays; }
      }
    }
    const age = diffDays(day, input.birthDate);
    const band = calved ? null : age < a.calfMilkDays ? 'calfDays' : age < 365 ? 'youngDays' : 'heiferDays';
    const targets: Bucket[] = [];
    if (day < end) targets.push(buckets[monthIndex]!);
    if (firstCalving && day < firstCalving) targets.push(rear);
    for (const target of targets) {
      target.days++;
      if (aiDates.has(day)) target.aiSantim += aiCost;
      if (band) { target[band]++; if (band === 'calfDays') target.calfMilkMl += a.calfMilkMlPerDay; }
      if (target !== rear) {
        target.producedMl += ml; target.soldMl += colostrum ? 0 : ml;
        if (calved) target.cowDays++;
        if (calvings.includes(day)) target.calves++;
      }
    }
  }
  const youngCost = (b: Bucket) => calfFeed * b.calfDays + youngFeed * b.youngDays + heiferFeed * b.heiferDays;
  let cumulative = -input.purchasePriceSantim;
  let paybackMonth: number | null = null;
  const months = buckets.map((b, m): ProjectionMonth => {
    const row = {
      month: m + 1, from: monthStarts[m]!, to: monthStarts[m + 1]!,
      producedMl: b.producedMl, soldMl: b.soldMl, calfMilkMl: b.calfMilkMl,
      milkIncomeSantim: milkMoney(b.soldMl, price), calfSalesSantim: b.calves * calfValue,
      feedSantim: feedCow * b.cowDays + milkMoney(b.producedMl, feedLiter),
      calfMilkSantim: milkMoney(b.calfMilkMl, price), youngStockFeedSantim: youngCost(b),
      vetSantim: annualCost(a.vetPerAnimalYearSantim, b.days), laborSantim: annualCost(a.laborPerMonthSantim, b.days, 12), breedingSantim: b.aiSantim,
    };
    const netSantim = row.milkIncomeSantim + row.calfSalesSantim - row.feedSantim - row.calfMilkSantim - row.youngStockFeedSantim - row.vetSantim - row.laborSantim - row.breedingSantim;
    cumulative += netSantim;
    if (paybackMonth === null && cumulative >= 0) paybackMonth = m + 1;
    return { ...row, netSantim, cumulativeSantim: cumulative };
  });
  const totalKeys = [...moneyLines, 'producedMl', 'soldMl', 'calfMilkMl'] as const;
  const totals = Object.fromEntries(totalKeys.map((key) => [key, months.reduce((sum, row) => sum + row[key], 0)])) as ProjectionTotals;
  const numerator = totals.feedSantim + totals.youngStockFeedSantim + totals.vetSantim + totals.laborSantim + totals.breedingSantim - totals.calfSalesSantim;
  const denominator = totals.soldMl - totals.calfMilkMl;
  const inHorizon = (date: string) => date >= start && date < end;
  const dryOffs = lactations.flatMap((date) => { const next = calvings.find((calving) => calving > date); return next ? [addDays(next, -settings.dryPeriodDays)] : []; }).filter(inHorizon);
  const costToFirstCalvingSantim = youngCost(rear) + milkMoney(rear.calfMilkMl, price) + annualCost(a.vetPerAnimalYearSantim, rear.days) + annualCost(a.laborPerMonthSantim, rear.days, 12) + rear.aiSantim;
  return {
    scenario, months, totals, operatingProfitSantim: totals.netSantim,
    netAfterPurchaseSantim: totals.netSantim + (input.endValueSantim ?? 0) - input.purchasePriceSantim,
    paybackMonth, breakEvenMilkPriceSantim: denominator <= 0 ? null : numerator <= 0 ? 0 : rounded(BigInt(numerator) * 1000n, BigInt(denominator)),
    impliedPeakMl: Math.round(1000 * woodY(matureA, settings.woodB / settings.woodC, settings.woodB, settings.woodC)),
    calvings: calvings.filter(inHorizon), conceptions: timeline.map((event) => event.conception).filter(inHorizon), dryOffs,
    rearing: firstCalving ? { firstCalving, ageAtFirstCalvingMonths: Math.round(diffDays(firstCalving, input.birthDate) / 30.4375 * 10) / 10, costToFirstCalvingSantim, costIncludingPurchaseSantim: costToFirstCalvingSantim + input.purchasePriceSantim } : null,
  };
}

export function projectAllScenarios(input: ProjectionInputData, resolved: ResolvedProjectionAssumptions, settings: TenantSettings) {
  return { low: projectAnimal(input, resolved, settings, 'LOW'), expected: projectAnimal(input, resolved, settings, 'EXPECTED'), high: projectAnimal(input, resolved, settings, 'HIGH') };
}

export type ProjectionWarning = 'SELLER_YIELD' | 'IMPLAUSIBLE_PEAK' | 'YOUNG_PREGNANT_HEIFER';
export function projectionWarnings(input: ProjectionInputData, result: ProjectionResult, herdMaxPeakMl: number | null): ProjectionWarning[] {
  const warnings: ProjectionWarning[] = [];
  if (input.kind === 'COW' && input.milking && !input.measuredByOwner) warnings.push('SELLER_YIELD');
  if (result.impliedPeakMl > 35000 || (herdMaxPeakMl !== null && result.impliedPeakMl * 2 > herdMaxPeakMl * 3)) warnings.push('IMPLAUSIBLE_PEAK');
  if (input.kind === 'HEIFER' && (input.serviceDate || input.monthsPregnant !== undefined) && input.startDate < addMonths(input.birthDate, 13)) warnings.push('YOUNG_PREGNANT_HEIFER');
  return warnings;
}
